/**
 * Links panel (spec §8.11): outgoing links live from Loro, incoming links from the graph service
 * (online only). Derived suggestions are shown distinctly once extraction lands (M6). Outgoing links
 * can be grouped as alternatives with a choice cardinality (option groups, spec §7.6.1, M10).
 */
import { Link2, Ruler, Trash2 } from "lucide-react";
import { useState } from "react";
import { useIncomingLinks } from "../api/hooks";
import { parseIri } from "../lib/iri";
import type { DocHandle } from "../sync/docManager";
import { useLoroSelector } from "../sync/hooks";
import { Button } from "../ui/Button";
import { sameFacts, type FactRow } from "../blocks/facts";
import { kindLabel, QUANTITYKIND_NS, UNIT_NS, UNITLESS, unitSymbol } from "../lib/quantities";
import { TermPicker } from "../ui/TermPicker";
import { useTermLabel } from "./useRoleLabel";
import { LinkDialog } from "./LinkDialog";
import { describeLiteral } from "./literals";
import type { LinkEntry } from "./model";
import type { LinkSubject } from "./subject";
import { useT } from "../i18n";
import { PRESETS, cardinalityMessage, createGroup, describeCardinality, dissolveGroup, readGroups, removeMember, setChosen, type CardinalityPreset, type OptionGroup } from "./groups";

export function LinksPanel({
  subject,
  wsHandle,
  workspaceId,
  userId,
  readOnly,
  onClose,
  title,
}: {
  subject: LinkSubject;
  wsHandle: DocHandle | null;
  workspaceId: string;
  userId: string;
  readOnly: boolean;
  onClose(): void;
  /** shown above the IRI: a page's title (§8.25) */
  title?: string;
}) {
  const [dialog, setDialog] = useState(false);
  const iri = subject.iri;
  const block = subject.block;
  const links =
    useLoroSelector(
      subject.doc,
      (d) => subject.readLinks(d),
      (a, b) => a.length === b.length && a.every((x, i) => x.id === b[i]!.id && x.p === b[i]!.p && x.o.value === b[i]!.o.value),
    ) ?? [];
  // option groups are a block's (§7.6.1); a page has none
  const groups =
    useLoroSelector(
      block?.handle.doc ?? null,
      (d) => (block ? readGroups(d, block.treeId) : []),
      (a, b) => JSON.stringify(a) === JSON.stringify(b),
    ) ?? [];
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [grouping, setGrouping] = useState(false);
  const grouped = new Set(groups.flatMap((g) => g.members));
  const byId = new Map(links.map((l) => [l.id, l]));
  const ungrouped = links.filter((l) => !grouped.has(l.id));
  const t = useT();
  const toggle = (id: string) => setSelected((cur) => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const facts = useLoroSelector(subject.doc, (d) => subject.readFacts(d), (a, b) => sameFacts(a, b)) ?? [];
  const incoming = useIncomingLinks(workspaceId, iri);
  const incomingRows = (incoming.data?.results.results?.bindings ?? []).filter((r) => r.o?.value === iri && r.s?.value !== iri);

  return (
    <aside className="w-80 shrink-0 overflow-auto border-l border-neutral-200 bg-[var(--surface-2)] p-4 text-sm dark:border-neutral-800" data-testid="links-panel">
      <div className="mb-2 flex items-center gap-2">
        <Link2 size={16} />
        <span className="font-semibold">{t("links.title")}</span>
        <div className="grow" />
        <button className="text-xs underline" onClick={onClose}>{t("links.close")}</button>
      </div>
      {title && <p className="mb-0.5 font-medium" data-testid="links-panel-title">{title}</p>}
      <p className="mb-2 break-all text-xs text-neutral-500">{iri}</p>

      <h3 className="mt-3 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">{t("links.outgoing")}</h3>
      {links.length === 0 && <p className="text-neutral-500">{t("links.none")}</p>}
      {groups.map((g) => (
        <GroupBox
          key={g.id}
          group={g}
          links={byId}
          readOnly={readOnly}
          onChoose={(linkId, on) => block && setChosen(block.handle.doc, block.treeId, g.id, linkId, on)}
          onRemoveMember={(linkId) => block && removeMember(block.handle.doc, block.treeId, g.id, linkId)}
          onDissolve={() => block && dissolveGroup(block.handle.doc, block.treeId, g.id)}
          onRemoveLink={(linkId) => subject.removeLink(linkId)}
        />
      ))}
      <ul className="space-y-1" data-testid="outgoing-links">
        {ungrouped.map((l) => (
          <li key={l.id} className="rounded-sm border border-neutral-200 p-1.5 dark:border-neutral-800" data-testid="outgoing-link">
            <div className="flex items-start gap-1">
              {!readOnly && block && <input type="checkbox" className="mt-1" checked={selected.has(l.id)} onChange={() => toggle(l.id)} data-testid="link-select" aria-label={t("links.select")} />}
              <LinkText link={l} />
              {!readOnly && (
                <button className="text-neutral-400 hover:text-red-700" title={t("links.remove")} data-testid="remove-link" onClick={() => subject.removeLink(l.id)}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {!readOnly && selected.size >= 2 && !grouping && (
        <Button className="mt-2" variant="ghost" onClick={() => setGrouping(true)} data-testid="group-alternatives">
          {t("links.groupAsAlternatives")}
        </Button>
      )}
      {grouping && (
        <GroupForm
          count={selected.size}
          onCancel={() => setGrouping(false)}
          onCreate={(label, min, max) => {
            const order = links.map((l) => l.id).filter((id) => selected.has(id));
            if (block) createGroup(block.handle.doc, block.treeId, order, { label, minChoice: min, maxChoice: max }, userId);
            setSelected(new Set());
            setGrouping(false);
          }}
        />
      )}
      {!readOnly && (
        <Button className="mt-2" onClick={() => setDialog(true)} data-testid="add-link">
          {t("block.menu.link")}
        </Button>
      )}

      <h3 className="mt-4 flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
        <Ruler size={12} /> {t("links.facts")}
      </h3>
      <p className="mb-1 text-xs text-neutral-500">{t("links.facts.lead")}</p>
      <ul className="space-y-1" data-testid="facts">
        {facts.map((f) => (
          <FactRowView
            key={f.id}
            fact={f}
            readOnly={readOnly}
            onChange={(patch) => subject.putFact(userId, patch, f.id)}
            onRemove={() => subject.removeFact(f.id)}
          />
        ))}
        {facts.length === 0 && <li className="text-neutral-500">{t("links.noneShort")}</li>}
      </ul>
      {!readOnly && (
        <Button className="mt-2" variant="ghost" data-testid="add-fact" onClick={() => subject.putFact(userId, { kind: "", value: "", unit: null, note: "" })}>
          {t("links.facts.add")}
        </Button>
      )}

      <h3 className="mt-4 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">{t("links.incoming")}</h3>
      {incoming.isError && <p className="text-neutral-500">{t("links.incomingNeedServer")}</p>}
      {incoming.data && incomingRows.length === 0 && <p className="text-neutral-500">{t("links.noneShort")}</p>}
      <ul className="space-y-1" data-testid="incoming-links">
        {incomingRows.map((r, i) => (
          <li key={i} className="rounded-sm border border-neutral-200 p-1.5 dark:border-neutral-800">
            <div className="truncate font-medium">{short(r.p!.value, t)}</div>
            <div className="truncate text-xs text-neutral-500" title={r.s!.value}>
              {t("links.from", { source: short(r.s!.value, t) })}
            </div>
            <div className="text-xs text-neutral-400">{provenance(r.g?.value, t)}</div>
          </li>
        ))}
      </ul>

      {dialog && (
        <LinkDialog subject={subject} wsHandle={wsHandle} workspaceId={workspaceId} userId={userId} onClose={() => setDialog(false)} />
      )}
    </aside>
  );
}

function LinkText({ link: l }: { link: LinkEntry }) {
  const t = useT();
  return (
    <div className="min-w-0 grow">
      <div className="truncate font-medium">{short(l.p, t)}</div>
      <div className={(l.o.kind === "literal" && l.o.value.length > 80 ? "whitespace-pre-wrap break-words" : "truncate") + " text-xs text-neutral-500"} title={l.o.value}>
        {l.o.kind === "literal" ? describeLiteral(l.o) : short(l.o.value, t)}
      </div>
      {l.note && <div className="text-xs italic text-neutral-500">{l.note}</div>}
      {l.derivedFrom && <span className="text-xs text-purple-700">{t("links.promoted")}</span>}
    </div>
  );
}

/** One fact: a quantity kind, a value, a unit and a note (spec §7.6.2, §8.11). */
function FactRowView({ fact: f, readOnly, onChange, onRemove }: { fact: FactRow; readOnly: boolean; onChange(patch: Partial<FactRow>): void; onRemove(): void }) {
  const t = useT();
  const aboutLabel = useTermLabel(f.about ?? "");
  return (
    <li className="rounded-sm border border-neutral-200 p-1.5 dark:border-neutral-800" data-testid="fact">
      {readOnly ? (
        <div className="text-xs">
          <span className="font-medium">{kindLabel(f.kind) || t("links.facts.noKind")}</span> {f.value} {unitSymbol(f.unit)}
          {f.about && <FactAbout iri={f.about} />}
          {f.note && <div className="italic text-neutral-500">{f.note}</div>}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1">
          <TermPicker
            value={{ iri: f.kind || null, label: kindLabel(f.kind) }}
            ns={QUANTITYKIND_NS}
            placeholder={t("links.facts.kind")}
            readOnly={readOnly}
            testId="fact-kind"
            onPick={(term) => term.iri && onChange({ kind: term.iri })}
          />
          <input
            className="w-20 kss-control kss-field bg-transparent px-2 py-1 text-xs"
            value={f.value}
            placeholder={t("links.facts.value")}
            data-testid="fact-value"
            onChange={(e) => onChange({ value: e.target.value })}
          />
          {!UNITLESS.has(f.kind) && (
            <TermPicker
              value={{ iri: f.unit, label: unitSymbol(f.unit) }}
              ns={UNIT_NS}
              placeholder={t("links.facts.unit")}
              readOnly={readOnly}
              testId="fact-unit"
              onPick={(term) => onChange({ unit: term.iri })}
            />
          )}
          <TermPicker
            value={{ iri: f.about ?? null, label: aboutLabel }}
            kind="class"
            placeholder={t("links.facts.about")}
            readOnly={readOnly}
            testId="fact-about"
            onPick={(term) => onChange({ about: term.iri ?? undefined })}
          />
          <input
            className="min-w-0 grow kss-control kss-field bg-transparent px-2 py-1 text-xs"
            value={f.note}
            placeholder={t("links.facts.note")}
            data-testid="fact-note"
            onChange={(e) => onChange({ note: e.target.value })}
          />
          <button className="text-neutral-400 hover:text-red-700" title={t("links.facts.remove")} data-testid="remove-fact" onClick={onRemove}>
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </li>
  );
}

/** [M31] "about water": the entity of the block a fact describes (Q92). */
function FactAbout({ iri }: { iri: string }) {
  const t = useT();
  const label = useTermLabel(iri);
  return (
    <span className="text-neutral-500" title={iri} data-testid="fact-about-label">
      {" "}
      {t("links.facts.aboutLabel", { term: label })}
    </span>
  );
}

/** A labelled bracket over the member rows; radios when one choice is allowed, checkboxes otherwise. */
function GroupBox({ group: g, links, readOnly, onChoose, onRemoveMember, onDissolve, onRemoveLink }: { group: OptionGroup; links: Map<string, LinkEntry>; readOnly: boolean; onChoose(linkId: string, on: boolean): void; onRemoveMember(linkId: string): void; onDissolve(): void; onRemoveLink(linkId: string): void }) {
  const t = useT();
  const members = g.members.map((id) => links.get(id)).filter((l): l is LinkEntry => !!l);
  const card = cardinalityMessage(g.minChoice, g.maxChoice);
  const single = g.maxChoice === 1;
  const over = g.maxChoice !== null && g.chosen.filter((c) => links.has(c)).length > g.maxChoice;
  return (
    <div className="mb-2 rounded-r border-l-4 border-[#a08a4d] bg-[#a08a4d]/5 py-1 pl-2 pr-1" data-testid="option-group" data-cardinality={describeCardinality(g.minChoice, g.maxChoice)}>
      <div className="flex items-center gap-2 text-xs">
        <span className="font-semibold" data-testid="option-group-label">{g.label ?? t("links.group.alternatives")}</span>
        <span className="text-neutral-500" data-testid="option-group-cardinality">{t(card.key, card.params)}</span>
        {over && <span className="rounded bg-amber-100 px-1 text-amber-900" data-testid="option-group-over">{t("links.group.tooMany")}</span>}
        <div className="grow" />
        {!readOnly && <button className="underline" onClick={onDissolve} data-testid="option-group-dissolve">{t("links.group.dissolve")}</button>}
      </div>
      <ul className="mt-1 space-y-1">
        {members.map((l) => (
          <li key={l.id} className="flex items-start gap-1 rounded-sm border border-neutral-200 bg-[var(--surface-2)] p-1.5 dark:border-neutral-800" data-testid="outgoing-link">
            <input
              type={single ? "radio" : "checkbox"}
              name={`group-${g.id}`}
              className="mt-1"
              checked={g.chosen.includes(l.id)}
              disabled={readOnly}
              onChange={(e) => onChoose(l.id, single ? true : e.target.checked)}
              data-testid="option-choose"
              aria-label={t("links.group.choose")}
            />
            <LinkText link={l} />
            {!readOnly && (
              <span className="flex flex-col items-end gap-1">
                <button className="text-[10px] text-neutral-500 underline" title={t("links.group.takeOut")} onClick={() => onRemoveMember(l.id)} data-testid="option-ungroup">{t("links.group.ungroup")}</button>
                <button className="text-neutral-400 hover:text-red-700" title={t("links.remove")} data-testid="remove-link" onClick={() => onRemoveLink(l.id)}>
                  <Trash2 size={14} />
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function GroupForm({ count, onCancel, onCreate }: { count: number; onCancel(): void; onCreate(label: string, min: number, max: number | null): void }) {
  const t = useT();
  const [label, setLabel] = useState("");
  const [preset, setPreset] = useState<CardinalityPreset>("exactlyOne");
  const [min, setMin] = useState(1);
  const [max, setMax] = useState<string>(String(count));
  const range = preset === "custom" ? { min, max: max.trim() === "" ? null : Number(max) } : PRESETS[preset];
  const valid = range.min >= 0 && (range.max === null || (Number.isInteger(range.max) && range.max >= Math.max(1, range.min)));
  return (
    <div className="mt-2 space-y-1 rounded border border-neutral-300 p-2 text-xs dark:border-neutral-700" data-testid="group-form">
      <input className="w-full rounded border border-neutral-300 bg-transparent px-1 py-0.5 dark:border-neutral-700" placeholder={t("links.group.labelPlaceholder")} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="group-label" />
      <select className="w-full rounded border border-neutral-300 bg-transparent px-1 py-0.5 dark:border-neutral-700" value={preset} onChange={(e) => setPreset(e.target.value as CardinalityPreset)} data-testid="group-cardinality">
        <option value="exactlyOne">{t("links.group.exactlyOne")}</option>
        <option value="atLeastOne">{t("links.group.atLeastOne")}</option>
        <option value="any">{t("links.group.any")}</option>
        <option value="custom">{t("links.group.custom")}</option>
      </select>
      {preset === "custom" && (
        <div className="flex items-center gap-1">
          <input type="number" min={0} className="w-14 rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={min} onChange={(e) => setMin(Math.max(0, Number(e.target.value)))} data-testid="group-min" />
          ..
          <input type="number" min={1} className="w-14 rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" placeholder="∞" value={max} onChange={(e) => setMax(e.target.value)} data-testid="group-max" />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button className="underline" onClick={onCancel}>{t("links.group.cancel")}</button>
        <Button disabled={!valid} onClick={() => onCreate(label, range.min, range.max)} data-testid="group-create">{t("links.group.create", { count })}</Button>
      </div>
    </div>
  );
}

function short(value: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const parsed = parseIri(value);
  if (parsed?.kind === "block") return t("links.short.block", { id: parsed.treeId ?? "" });
  if (parsed?.kind === "concept") return t("links.short.concept", { id: parsed.id?.slice(0, 8) ?? "" });
  if (parsed?.kind === "doc") return t("links.short.page");
  const tail = value.split(/[#/]/).pop();
  return tail || value;
}

function provenance(graph: string | undefined, t: (key: string) => string): string {
  if (!graph) return "";
  if (graph.includes(":asserted:")) return t("links.prov.asserted");
  if (graph.includes(":computed:")) return t("links.prov.computed");
  if (graph.includes(":derived:")) return t("links.prov.derived");
  return t("links.prov.ontology");
}

export type { LinkEntry };
